import type { Event, EventEnvelope, EventPayload, ElementAttributes } from './types';
import { normalizePath } from './utils/normalizePath';
import { sanitizeElementName } from './utils/sanitizeElementName';

const SESSION_STORAGE_KEY = 'gws_flows_session_id';

/**
 * Get or create a session ID for Amplitude.
 *
 * Amplitude requires session_id to be an integer (so we'll use a Unix timestamp).
 * Using sessionStorage ensures consistent ID across all events in the same browser tab,
 * works in iframes (unlike cookies which are blocked), automatically clears when tab
 * closes (natural session boundary), and persists across Turbo navigations.
 *
 * For grouping events by flow, use custom.gws_flows.flow_id instead.
 */
function getOrCreateSessionId(): string {
  try {
    let sessionId = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (!sessionId) {
      sessionId = Date.now().toString();
      sessionStorage.setItem(SESSION_STORAGE_KEY, sessionId);
    }
    return sessionId;
  } catch {
    // sessionStorage might be blocked in some contexts, fall back to timestamp
    return Date.now().toString();
  }
}

function getElementAttributes(element: Element): ElementAttributes {
  const textContent = element.textContent?.trim() || '';
  const tagName = element.tagName.toLowerCase();

  return {
    element_name: sanitizeElementName(textContent) || tagName,
    element_type: tagName,
    element_href: (element as HTMLAnchorElement).href
      ? normalizePath(new URL((element as HTMLAnchorElement).href).pathname)
      : undefined,
    parent_name: undefined,
    parent_type: undefined,
  };
}

/**
 * Build the EventEnvelope structure for analytics events.
 * Matches ZenPayroll's schema for cross-platform analytics compatibility.
 */
export function buildEnvelope<T extends EventPayload>(
  event: Event<T>,
  target?: Element | null,
): EventEnvelope {
  const context = window.ANALYTICS_CONTEXT || {};
  const zpCompanyUuid = context.company?.zp_company_uuid || null;
  const flowId = context.flow?.id || null;

  return {
    application: {
      app: 'gws-flows',
    },
    user: {
      // CRITICAL: user_id is required by Amplitude and by gusto-analytics's hasValidIdentifiers check + schema compatibility
      user_id: zpCompanyUuid,
      user_role_id: null,
      user_role_type: null,
    },
    view: {
      view_name: normalizePath(window.location.pathname),
      referrer_view_name: document.referrer
        ? normalizePath(new URL(document.referrer).pathname)
        : null,
    },
    element: target ? getElementAttributes(target) : null,
    custom: {
      gws_flows: {
        company_uuid: zpCompanyUuid,
        flow_id: flowId, // Flow UUID for session grouping in Amplitude
        flow_type: context.flow?.flow_type,
        partner: context.partner,
        entity_type: context.flow?.zp_entity_type,
        // no entity uuid, because we cannot get opt-out tracking consent for employee or contractor flows
        is_iframed: window.self !== window.top,
      },
    },
    payload: event.payload,
    session_id: getOrCreateSessionId(),
  };
}
